import React from 'react'
import styled, { keyframes } from 'styled-components'
import * as RadixToast from '@radix-ui/react-toast'
import { IoClose } from 'react-icons/io5'
import { Theme } from '../../../typings/styled'
import type { ToastAction } from './types'

const slideIn = keyframes`
  from {
    transform: translateX(100%);
  }
  to {
    transform: translateX(0);
  }
`

const slideOut = keyframes`
  from {
    transform: translateX(0);
  }
  to {
    transform: translateX(100%);
  }
`

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`

const fadeOut = keyframes`
  from {
    opacity: 1;
  }
  to {
    opacity: 0;
  }
`

const StyledViewport = styled(RadixToast.Viewport)`
  position: fixed;
  bottom: 24px;
  right: 24px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 390px;
  max-width: 100vw;
  margin: 0;
  list-style: none;
  z-index: 9999;
  outline: none;

  @media (max-width: 640px) {
    width: 100%;
    right: 0;
    bottom: 0;
    padding: 8px;
  }
`

interface ToastVariantProps {
  variant?: 'success' | 'error' | 'info' | 'warning'
}

const getVariantColor = (
  variant: ToastVariantProps['variant'],
  theme: Theme,
) => {
  switch (variant) {
    case 'success':
      return theme.colors.system.success.regular
    case 'error':
      return theme.colors.system.critical.regular
    case 'warning':
      return theme.colors.system.warning.regular
    case 'info':
    default:
      return theme.colors.system.highlight.regular
  }
}

const StyledToast = styled(RadixToast.Root)<ToastVariantProps>`
  background-color: ${({ theme }) => theme.colors.surface.background};
  border-radius: 4px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  padding: 16px;
  display: flex;
  gap: 8px;
  align-items: flex-start;
  border-left: 4px solid
    ${({ theme, variant }) => getVariantColor(variant, theme)};

  &[data-state='open'] {
    animation:
      ${slideIn} 150ms cubic-bezier(0.16, 1, 0.3, 1),
      ${fadeIn} 150ms cubic-bezier(0.16, 1, 0.3, 1);
  }

  &[data-state='closed'] {
    animation:
      ${slideOut} 100ms cubic-bezier(0.16, 1, 0.3, 1),
      ${fadeOut} 100ms cubic-bezier(0.16, 1, 0.3, 1);
  }

  &[data-swipe='move'] {
    transform: translateX(var(--radix-toast-swipe-move-x));
  }

  &[data-swipe='cancel'] {
    transform: translateX(0);
    transition: transform 200ms ease-out;
  }

  &[data-swipe='end'] {
    animation: ${slideOut} 100ms cubic-bezier(0.16, 1, 0.3, 1);
  }
`

const StyledTitle = styled(RadixToast.Title)`
  font-weight: 600;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.font.regular};
  line-height: 1;
  margin-bottom: 4px;
`

const StyledDescription = styled(RadixToast.Description)`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.font.subdued};
  line-height: 1.4;
  margin: 0;
`

const StyledClose = styled(RadixToast.Close)`
  position: absolute;
  right: 8px;
  top: 8px;
  background: none;
  border: none;
  padding: 4px;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.font.subdued};
  transition: color 150ms ease;

  &:hover {
    color: ${({ theme }) => theme.colors.font.regular};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.system.highlight.regular};
    outline-offset: 2px;
    border-radius: 2px;
  }
`

const ContentContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding-right: 24px;
`

const ActionContainer = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 8px;
`

const StyledAction = styled(RadixToast.Action)`
  background: ${({ theme }) => theme.colors.system.highlight.regular};
  color: ${({ theme }) => theme.colors.font.white};
  border: none;
  padding: 4px 8px;
  border-radius: 2px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 150ms ease;

  &:hover {
    background: ${({ theme }) => theme.colors.system.highlight.hover};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.system.highlight.regular};
    outline-offset: 2px;
  }
`

interface Props extends RadixToast.ToastProps {
  title?: string
  description?: string
  action?: ToastAction
  variant?: 'success' | 'error' | 'info' | 'warning'
}

const Toast: React.FC<Props> = ({
  title,
  description,
  action,
  variant = 'info',
  ...props
}) => {
  return (
    <StyledToast variant={variant} {...props}>
      <ContentContainer>
        {title && <StyledTitle>{title}</StyledTitle>}
        {description && <StyledDescription>{description}</StyledDescription>}
        {action && (
          <ActionContainer>
            <StyledAction altText={action.altText} onClick={action.onClick}>
              {action.label}
            </StyledAction>
          </ActionContainer>
        )}
      </ContentContainer>
      <StyledClose aria-label="Close">
        <IoClose size={16} />
      </StyledClose>
    </StyledToast>
  )
}

export { Toast, StyledViewport as ToastViewport, RadixToast as ToastPrimitive }
